import { Vue, Component, Ref } from 'vue-property-decorator'
import AttachmentsTree from '@/modules/viewerModule/attachmentsTree/AttachmentsTree.vue'
import { initialViewerModuleState, IViewerModuleState, ViewerType, BROADCAST_ATTACHMENT_UPDATED_CHANNEL, BROADCAST_ATTACHMENT_UPDATED_CHANNEL_MESSAGE } from './ViewerModule.models'
import Toolbox from '@/modules/viewerModule/toolbox/Toolbox.vue'
import ToolboxComponent from '@/modules/viewerModule/toolbox/Toolbox'
import Viewer from '@/modules/viewerModule/viewer/Viewer.vue'
import ViewerComponent from '@/modules/viewerModule/viewer/Viewer'
import { ToolType } from './viewer/tools/Tool'
import { ConfigurationManager, FeaturesManager, ImageType, ViewerUserConfiguration } from './viewer/Viewer.models'
import SampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import CustomConfigResource from '@/api/resources/ui/CustomConfigResource'
import SubportionAttachmentService from '@/api/resources/attachment/subportionAttachmentService'
import sampleProcessingSampleService from '@/modules/sampleProcessingModule/services/sampleProcessingSampleService'
import { Dictionary } from 'vue-router/types/router'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { AttachmentResponse } from '@/api/services/common/attachment/common-attachment.dto'
import ModalUnsavedChanges from '@/components/modals/ModalUnsavedChanges.vue'
import { LiquorTreeNode } from 'liquor-tree'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: { AttachmentsTree, Toolbox, Viewer, ModalUnsavedChanges }
})
export default class ViewerModule extends Vue implements FeaturesManager, ConfigurationManager {
    private state: IViewerModuleState;
    @Ref('viewer') readonly viewer!: ViewerComponent
    @Ref('toolbox') readonly toolbox!: ToolboxComponent
    @Ref('attachmentsTree') readonly attachmentsTree!: any
    @Ref('unsavedChangesModal') readonly unsavedChangesModal!: any
    public featuresManager!: FeaturesManager;
    private flagLoading: boolean = true
    public configurationManager!: ConfigurationManager
    private broadcastChannel!: BroadcastChannel

    constructor() {
        super()
        this.state = initialViewerModuleState()
    }

    created():void {
        this.loadAttachment(this.$route.query)
        this.featuresManager = this
        this.broadcastChannel = new BroadcastChannel(BROADCAST_ATTACHMENT_UPDATED_CHANNEL)
        window.onbeforeunload = (event:BeforeUnloadEvent) => {
            this.handleCloseWindow(event)
        }
    }

    beforeDestroy():void {
        this.broadcastChannel.close()
    }

    private getViewerType(routeQuery: Dictionary<string | (string | null)[]>): ViewerType {
        if (routeQuery.viewerType === ViewerType.WSI_VIEWER) {
            return ViewerType.WSI_VIEWER
        } else {
            return ViewerType.STATIC_VIEWER
        }
    }

    private async loadAttachment(routeQuery: Dictionary<string | (string | null)[]>):Promise<void> {
        this.state.query = routeQuery
        this.flagLoading = true
        this.state.attachmentInfo = {
            laboratoryId: Number(routeQuery.laboratoryId) || 1,
            caseId: Number(routeQuery.caseId),
            sampleId: Number(routeQuery.sampleId),
            portionId: Number(routeQuery.portionId),
            subportionId: Number(routeQuery.subportionId),
            attachmentId: (routeQuery.attachmentId ? Number(routeQuery.attachmentId) : undefined),
            viewerType: this.getViewerType(routeQuery),
            imageInfo: {} as any,
            wmtsImageInfo: {} as any
        }

        // loading attahcment details
        let attachmentDetails: AttachmentResponse, imageUrl, thumbnailUrl
        const isSubportion = !!this.state.attachmentInfo.subportionId
        let imageType: ImageType

        if (this.state.attachmentInfo.viewerType === ViewerType.STATIC_VIEWER) {
            if (isSubportion) {
                imageType = ImageType.MICRO
                attachmentDetails = await this.$_toCallSecure(SubportionAttachmentService.getAttachment(
                    this.state.attachmentInfo.laboratoryId,
                    this.state.attachmentInfo.caseId,
                    this.state.attachmentInfo.sampleId,
                    this.state.attachmentInfo.portionId,
                    this.state.attachmentInfo.subportionId,
                    this.state.attachmentInfo.attachmentId))

                imageUrl = SubportionAttachmentService.getUrlForAttachmentFiltered(
                    this.state.attachmentInfo.laboratoryId,
                    this.state.attachmentInfo.caseId,
                    this.state.attachmentInfo.sampleId,
                    this.state.attachmentInfo.portionId,
                    this.state.attachmentInfo.subportionId,
                    this.state.attachmentInfo.attachmentId)
            } else {
                imageType = ImageType.MACRO
                attachmentDetails = await this.$_toCallSecure(SampleAttachmentService.getAttachment(
                    this.state.attachmentInfo.laboratoryId,
                    this.state.attachmentInfo.caseId,
                    this.state.attachmentInfo.sampleId,
                    this.state.attachmentInfo.attachmentId))

                imageUrl = SampleAttachmentService.getUrlForAttachmentFiltered(
                    this.state.attachmentInfo.laboratoryId,
                    this.state.attachmentInfo.caseId,
                    this.state.attachmentInfo.sampleId,
                    this.state.attachmentInfo.attachmentId)
            }
        } else {
            imageType = ImageType.WSI
            attachmentDetails = await this.$_toCallSecure(SubportionAttachmentService.getWSIAttachmentDetails(
                this.state.attachmentInfo.laboratoryId,
                this.state.attachmentInfo.caseId,
                this.state.attachmentInfo.sampleId,
                this.state.attachmentInfo.portionId,
                this.state.attachmentInfo.subportionId,
                this.state.attachmentInfo.attachmentId ? this.state.attachmentInfo.attachmentId : 1))

            imageUrl = SubportionAttachmentService.getUrlForWMTSAttachment(
                this.state.attachmentInfo.laboratoryId,
                this.state.attachmentInfo.caseId,
                this.state.attachmentInfo.sampleId,
                this.state.attachmentInfo.portionId,
                this.state.attachmentInfo.subportionId,
                this.state.attachmentInfo.attachmentId ? this.state.attachmentInfo.attachmentId : 1)

            const wmtsFileInfo = await this.$_toCallSecure(SubportionAttachmentService.getWSIFileInfo(
                this.state.attachmentInfo.laboratoryId,
                this.state.attachmentInfo.caseId,
                this.state.attachmentInfo.sampleId,
                this.state.attachmentInfo.portionId,
                this.state.attachmentInfo.subportionId,
                this.state.attachmentInfo.attachmentId ? this.state.attachmentInfo.attachmentId : 1))
            this.state.attachmentInfo.wmtsImageInfo = wmtsFileInfo

            // fixing to obtain a map
            const headerKeys = Object.keys(wmtsFileInfo.headers)
            const headersMap = new Map<string, string>()
            headerKeys.forEach((header) => {
                const value = wmtsFileInfo.headers[header]
                headersMap.set(header.replace('openslide.', ''), value)
            })
            this.state.attachmentInfo.wmtsImageInfo.headers = headersMap
        }

        if (!attachmentDetails) {
            return
        }

        this.state.attachmentInfo.imageInfo = {
            type: imageType,
            mimetype: attachmentDetails.mimetype,
            width: attachmentDetails.imageWidth ?? 0,
            height: attachmentDetails.imageHeight ?? 0,
            url: imageUrl,
            length: attachmentDetails.length,
            label: <string>routeQuery.label,
            filename: attachmentDetails.filename,
            scale: attachmentDetails.scale ?? 0
        }

        // loading sample label if needed
        if (this.state.attachmentInfo.viewerType !== ViewerType.WSI_VIEWER && !this.state.attachmentInfo.imageInfo.label) {
            const sample = await this.$_toCallSecure(sampleProcessingSampleService.findById(this.state.attachmentInfo.laboratoryId, this.state.attachmentInfo.caseId, this.state.attachmentInfo.sampleId))
            if (sample) {
                this.state.attachmentInfo.imageInfo.label = sample.label
            }
        }

        this.flagLoading = false
        this.featuresManager = this
        this.configurationManager = this
    }

    onViewerMounted():void {
        this.toolbox.setViewer(this.viewer)
        this.toolbox.resendCurrentTool()
    }

    async loadConfiguration(): Promise<ViewerUserConfiguration> {
        let backendResponse = await CustomConfigResource.get('uicomponent.viewer.macro', this.state.attachmentInfo?.laboratoryId)
        if (backendResponse && backendResponse != null) {
            return backendResponse.config
        } else {
            return <any>null
        }
    }
    async saveConfiguration(configuration: ViewerUserConfiguration): Promise<void> {
        const [error] = await this.$_toCall(CustomConfigResource.save('uicomponent.viewer.macro', configuration, this.state.attachmentInfo?.laboratoryId))
        if (error) {
            this.$_showErrorModalFromErrorResponse(error, <string> this.$t('viewer.userconfig.saveError'))
        } else {
            this.$_showToastSuccess(<string> this.$t('viewer.userconfig.saveSuccess'))
        }
    }

    async loadFeatures(): Promise<any> {
        if (this.state.attachmentInfo?.attachmentId) {
            let response: any
            if (this.state.attachmentInfo.viewerType === ViewerType.STATIC_VIEWER) {
                if (this.state.attachmentInfo.sampleId && !this.state.attachmentInfo.subportionId) {
                    response = await this.$_toCallUnique(
                        SampleAttachmentService.loadSampleAnnotations(
                            this.state.attachmentInfo.laboratoryId,
                            this.state.attachmentInfo.caseId,
                            this.state.attachmentInfo.sampleId,
                            this.state.attachmentInfo.attachmentId),
                        error => this.$_showErrorModalFromErrorResponse(error, this.$tc('viewer.annotations.errors.loadingAnnotations'))
                    )
                } else if (this.state.attachmentInfo.subportionId) {
                    response = await this.$_toCallUnique(
                        SubportionAttachmentService.loadSubportionAnnotations(
                            this.state.attachmentInfo.laboratoryId,
                            this.state.attachmentInfo.caseId,
                            this.state.attachmentInfo.sampleId,
                            this.state.attachmentInfo.portionId,
                            this.state.attachmentInfo.subportionId,
                            this.state.attachmentInfo.attachmentId),
                        error => this.$_showErrorModalFromErrorResponse(error, this.$tc('viewer.annotations.errors.loadingAnnotations'))
                    )
                }
            } else {
                // TODO load features of WSI image type
            }

            if (response && response[0]) {
                return response
            }
        }
    }

    public changeToolFromViewer(tool: ToolType):void {
        this.toolbox.changeTool(tool)
    }

    public changeColor(color: string):void {
        this.viewer.selectColor(color)
    }

    public showMapEvent(showMap: boolean):void {
        this.toolbox.setShowMap(showMap)
    }

    public showFxEvent(showFx: boolean):void {
        this.toolbox.setShowFx(showFx)
    }

    private changesMade():void {
        this.state.flagChanges = true
    }

    private handleCloseWindow(event: BeforeUnloadEvent):void {
        if (this.state.flagChanges) {
            event.returnValue = 'changes'
        }
    }

    public async saveCurrentMeasureScale(): Promise<void> {
        let info = this.state.attachmentInfo
        if (info) {
            let error: any
            if (info.subportionId) {
                [error] = await this.$_toCall(SubportionAttachmentService.updateMeasureScale(info.laboratoryId, info.caseId, info.sampleId, info.portionId, info.subportionId, info.attachmentId, info.imageInfo.scale))
            } else {
                [error] = await this.$_toCall(SampleAttachmentService.updateMeasureScale(info.laboratoryId, info.caseId, info.sampleId, info.attachmentId, info.imageInfo.scale))
            }

            if (error) {
                this.$_showErrorModal(<string> this.$t('viewer.annotations.errors.savingAnnotations'))
            }
        }
    }

    public async saveFeatures(annotationId: string, attachmentFilename:string, geojsonFeatures: string, imageBytes: string, thumbnailBytes: string): Promise<string | undefined> {
        const info = this.state.attachmentInfo
        if (info) {
            let error: any
            let response: any
            if (annotationId) {
                if (info?.sampleId && !info.subportionId) {
                    [error, response] = await this.$_toCall(SampleAttachmentService.updateSampleAnnotations(info.laboratoryId, info.caseId, info.sampleId, info.attachmentId, annotationId, geojsonFeatures, imageBytes, thumbnailBytes, attachmentFilename))
                } else if (info.subportionId) {
                    [error, response] = await this.$_toCall(SubportionAttachmentService.updateSubportionAnnotations(info.laboratoryId, info.caseId, info.sampleId, info.portionId, info.subportionId, info.attachmentId, annotationId, geojsonFeatures, imageBytes, thumbnailBytes, attachmentFilename))
                }
            } else {
                if (info.sampleId && !info.subportionId) {
                    [error, response] = await this.$_toCall(SampleAttachmentService.createSampleAnnotations(info.laboratoryId, info.caseId, info.sampleId, info.attachmentId, geojsonFeatures, imageBytes, thumbnailBytes, attachmentFilename))
                } else if (info.subportionId) {
                    [error, response] = await this.$_toCall(SubportionAttachmentService.createSubportionAnnotations(info.laboratoryId, info.caseId, info.sampleId, info.portionId, info.subportionId, info.attachmentId, geojsonFeatures, imageBytes, thumbnailBytes, attachmentFilename))
                }
            }

            if (error) {
                this.$_showErrorModal(<string> this.$t('viewer.annotations.errors.savingAnnotations'))
            } else {
                info.attachmentId = response && response.attachmentId
                info.imageInfo.filename = attachmentFilename
                this.state.annotationId = response && response.annotationId
                this.broadcastChannel.postMessage(BROADCAST_ATTACHMENT_UPDATED_CHANNEL_MESSAGE)
                this.onAnnotationUpdated(attachmentFilename)
                this.$emit('annotationUpdated')
                this.$_showToastSuccess(<string> this.$t('component.modalShowSuccess.title'))
                this.state.flagChanges = false
                return this.state.annotationId
            }
        }
    }

    protected toogleAttachments(showAttachments: boolean):void {
        this.state.showTreeNavigation = showAttachments
    }

    protected onAttachmentsTreeClosed():void {
        this.state.showTreeNavigation = false
    }

    protected async onAttachmentSelected(query: Dictionary<string | StrictNullable<string>[]>, lastSelectedNode: LiquorTreeNode<unknown, unknown, unknown>):Promise<void> {
        const loadAttachments = () => {
            this.state.flagChanges = false
            this.state.query = query
            this.loadAttachment(query)
        }

        if (this.state.flagChanges) {
            this.unsavedChangesModal.askAndExecuteCallbacks(
                (): boolean => {
                    // save changes callback
                    lastSelectedNode.select()
                    this.viewer.requestSave()
                    return false
                },
                // don't save changes callback
                loadAttachments,
                // cancel callback
                (): void => {
                    lastSelectedNode.select()
                },
                this.$tc('viewer.unsavedChangesModal.text')
            )
        } else {
            loadAttachments()
        }
    }

    get selectedEntity() {
        let entityType = null
        if (this.state.attachmentInfo?.subportionId) {
            entityType = ATTACHMENT_ENTITY_TYPE.SUBPORTION
        } else if (this.state.attachmentInfo?.portionId) {
            entityType = ATTACHMENT_ENTITY_TYPE.PORTION
        } else if (this.state.attachmentInfo?.sampleId) {
            entityType = ATTACHMENT_ENTITY_TYPE.SAMPLE
        }
        return {
            id: this.state.attachmentInfo?.subportionId || this.state.attachmentInfo?.portionId || this.state.attachmentInfo?.sampleId,
            entityType: entityType
        }
    }

    public async onAnnotationUpdated(selectAttachmentFilename?: string): Promise<void> {
        if (this.state.showTreeNavigation) {
            await this.attachmentsTree.getHierarchicalCaseIndexTree()
            if (selectAttachmentFilename) {
                this.attachmentsTree.selectAttachmentByFileName(selectAttachmentFilename)
            }
        }
    }

    public onSelectedColor(color: string):void {
        this.state.selectedColor = color
    }
}
